import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { API } from "../../../config";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const useCartDetails = () => {
  const [showProductImages, setShowProductImages] = useState({
    open: false,
    productId: null,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { cartId } = useParams();
  const [cartDetails, setCartDetails] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showCancelOrderConfirmation, setShowCancelOrderConfirmation] =
    useState(false);
  const [showMarkOrderConfirmation, setShowMarkOrderConfirmation] =
    useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [cartName, setCartName] = useState("");
  const [cartNotes, setCartNotes] = useState("");

  const getCartDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/Admin/carts/${cartId}`);
      setCartDetails(response.data);
    } catch (error) {
      console.error("Failed to fetch cart details:", error);
      setCartDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeImages = async (type, images, productId) => {
    try {
      const response = await axios.put(
        `${API}/Admin/carts/${cartId}/products/${productId}`,
        {
          [type === "product" ? "productImages" : "mockupImages"]: images,
        },
      );
      if (response.status === 200) {
        toast.success("Images changed successfully");
        getCartDetails();
      } else {
        toast.error("Failed to change images");
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to change images");
    }
  };

  const updateCartNotes = async (data) => {
    try {
      const response = await axios.put(`${API}/Admin/carts/${cartId}`, {
        notes: data,
      });
      if (response.status === 200) {
        toast.success("Notes updated successfully");
        getCartDetails();
      } else {
        toast.error("Failed to update notes");
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to update cart");
    }
  };

  const updateCartProduct = async (productId, data) => {
    try {
      const response = await axios.put(
        `${API}/Admin/carts/${cartId}/products/${productId}`,
        data,
      );
      if (response.status === 200) {
        toast.success("Product updated successfully");
        getCartDetails();
      } else {
        toast.error("Failed to update product");
      }
    } catch (error) {
      console.error("Failed to update product:", error);
      toast.error("Failed to update product");
    }
  };

  useEffect(() => {
    getCartDetails();
  }, [cartId]);

  useEffect(() => {
    if (cartDetails?.cart) {
      setCartName(cartDetails.cart.name || "");
      setCartNotes(cartDetails.cart.notes ?? "");
    }
  }, [cartDetails?.cart?.name, cartDetails?.cart?.notes]);

  useEffect(() => {
    if (!cartDetails?.cart) return;
    if ((cartDetails.cart.notes ?? "") === cartNotes) return;
    const timer = setTimeout(() => {
      updateCartNotes(cartNotes);
    }, 3000);

    return () => clearTimeout(timer);
  }, [cartNotes]);

  const deleteCart = async () => {
    try {
      const response = await axios.delete(`${API}/Admin/carts/${cartId}`);
      if (response.status === 200) {
        toast.success("Cart deleted successfully");
        navigate(`/users/${cartDetails?.user?._id}/carts`);
      } else {
        toast.error("Failed to delete cart");
      }
    } catch (error) {
      console.error("Failed to delete cart:", error);
      toast.error("Failed to delete cart");
    }
  };

  const deleteProductFromCart = async (productId) => {
    try {
      const response = await axios.delete(
        `${API}/Admin/carts/${cartId}/products/${productId}`,
      );
      if (response.status === 200) {
        toast.success("Product deleted from cart successfully");
        getCartDetails();
      } else {
        toast.error("Failed to delete product from cart");
      }
    } catch (error) {
      console.error("Failed to delete product from cart:", error);
      toast.error("Failed to delete product from cart");
    }
  };

  const { productImages, mockupImages, defaultImage } = useMemo(() => {
    if (
      !cartDetails ||
      !cartDetails.cart ||
      !cartDetails.cart.products ||
      loading
    )
      return {
        productImages: [],
        mockupImages: [],
        defaultImage: "",
      };
    const product = cartDetails.cart.products.find(
      (product) => product._id === showProductImages.productId,
    );
    console.log(product, { showProductImages });
    return {
      productImages: product?.images?.product,
      mockupImages: product?.images?.mockups,
      defaultImage: product?.defaultImage || "",
    };
  }, [cartDetails, showProductImages, loading]);

  const handleCancelDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmDeleteConfirmation = async () => {
    setShowDeleteConfirmation(false);
    await deleteCart();
  };

  const handleAddDiscount = async (discountValue) => {
    try {
      const response = await axios.patch(
        `${API}/Admin/carts/${cartId}/discount/${discountValue}`,
      );
      if (response.status === 200) {
        toast.success("Discount applied successfully");
        getCartDetails();
      } else {
        toast.error("Failed to apply discount");
      }
    } catch (error) {
      console.error("Failed to apply discount:", error);
      toast.error("Failed to apply discount");
    }
  };

  const handleDownloadPdf = async () => {
    try {
      toast.info("Generating PDF...");

      const pdf = new jsPDF("p", "mm", "a4");
      const pageHeight = 297; // A4 height in mm
      const pageWidth = 210; // A4 width in mm
      const margin = 10;
      let currentY = margin;

      // Create temporary container
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.top = "0";
      tempContainer.style.width = `${pageWidth}mm`;
      document.body.appendChild(tempContainer);

      // Function to add element to PDF
      const addElementToPdf = async (element, isLastElement = false) => {
        const canvas = await html2canvas(element, {
          // Lower scale to reduce image size while keeping readability
          scale: 1.2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          width: element.offsetWidth,
          height: element.offsetHeight,
        });

        // Use JPEG with compression instead of PNG to keep PDF small
        const imgData = canvas.toDataURL("image/jpeg", 0.7);
        const imgWidth = pageWidth - 2 * margin;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Check if element fits on current page
        if (currentY + imgHeight > pageHeight - margin && currentY > margin) {
          // Add new page
          pdf.addPage();
          currentY = margin;
        }

        pdf.addImage(imgData, "JPEG", margin, currentY, imgWidth, imgHeight);
        currentY += imgHeight + 5; // Add some spacing

        return imgHeight;
      };

      // Render header
      const headerDiv = document.createElement("div");
      headerDiv.style.padding = "10px";
      headerDiv.style.width = `${pageWidth - 20}mm`;
      headerDiv.innerHTML = `
            <h1 style="font-size: 24px; margin-bottom: 10px;">
              ${cartDetails?.cart?.name || "Untitled Cart"}
            </h1>
            ${
              cartDetails?.user?.name || cartDetails?.user?.companyName
                ? `
              <p style="font-size: 14px; color: #666;">
                ${[cartDetails?.user?.name, cartDetails?.user?.companyName]
                  .filter(Boolean)
                  .join(" | ")}
              </p>
            `
                : ""
            }
            <p style="font-size: 12px; color: #999; margin-top: 5px;">
              Created On: ${new Date(
                cartDetails?.cart?.createdAt,
              ).toLocaleDateString()}
            </p>
            <h2 style="font-size: 18px; margin-top: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">
              PRODUCTS
            </h2>
          `;
      tempContainer.appendChild(headerDiv);
      await addElementToPdf(headerDiv);
      headerDiv.remove();

      // Render each product card
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const productDiv = document.createElement("div");
        productDiv.style.padding = "15px";
        productDiv.style.width = `${pageWidth - 20}mm`;
        productDiv.style.marginBottom = "10px";
        productDiv.style.borderBottom =
          i < products.length - 1 ? "1px solid #ddd" : "none";

        productDiv.innerHTML = `
              <div style="display: flex; gap: 15px;">
                ${
                  product.image
                    ? `
                  <img 
                    src="${product.image}" 
                    alt="${product.name}"
                    style="width: 100px; height: 100px; object-fit: cover; flex-shrink: 0;"
                    crossorigin="anonymous"
                  />
                `
                    : ""
                }
                <div style="flex: 1;">
                  <h3 style="font-size: 16px; margin-bottom: 5px;">
                    ${product.name || "-"}
                  </h3>
                  <p style="font-size: 12px; color: #666; margin-bottom: 8px;">
                    Brand: ${product.brand || "-"}
                  </p>
                  
                  ${
                    product.description && product.description !== "-"
                      ? `
                    <p style="font-size: 11px; margin-bottom: 8px;">
                      Description: ${product.description}
                    </p>
                  `
                      : ""
                  }
                  
                  ${
                    product.itemDetails && product.itemDetails.length > 0
                      ? `
                    <div style="font-size: 11px; margin-bottom: 8px;">
                      <strong>Item Details:</strong>
                      <ul style="margin-left: 20px; margin-top: 4px;">
                        ${product.itemDetails
                          .map((detail) => `<li>${detail}</li>`)
                          .join("")}
                      </ul>
                    </div>
                  `
                      : ""
                  }
      
                  ${
                    product.customisation || product.remarks
                      ? `
                    <div style="font-size: 10px; color: #666; margin-top: 8px;">
                      ${
                        product.customisation
                          ? `<p>Customisation: ${product.customisation}</p>`
                          : ""
                      }
                      ${
                        product.remarks
                          ? `<p>Remarks: ${product.remarks}</p>`
                          : ""
                      }
                    </div>
                  `
                      : ""
                  }
      
                  <div style="margin-top: 12px; padding: 12px; border: 1px solid #ddd; background-color: #f9f9f9; border-radius: 4px;">
                    <div style="display: flex; justify-content: space-between; font-size: 11px;">
                      <div>
                        <p>Quantity: ${product.quantity || 0}</p>
                        <p>Rate: Rs ${product.price || 0}</p>
                        <p>GST: ${product.gst || 0}%</p>
                      </div>
                      <div style="text-align: right;">
                        <p style="font-weight: bold; font-size: 13px;">
                          Amount: Rs ${Math.round(
                            (product.price || 0) *
                              (1 + (product.gst || 0) / 100) *
                              (product.quantity || 0),
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            `;

        tempContainer.appendChild(productDiv);
        await addElementToPdf(productDiv, false);
        productDiv.remove();
      }

      // Render summary
      const summaryDiv = document.createElement("div");
      summaryDiv.style.padding = "20px";
      summaryDiv.style.width = `${pageWidth - 20}mm`;
      summaryDiv.style.border = "2px solid #333";
      summaryDiv.style.backgroundColor = "#f5f5f5";
      summaryDiv.style.borderRadius = "4px";
      summaryDiv.style.marginTop = "20px";

      summaryDiv.innerHTML = `
            <h2 style="font-size: 18px; margin-bottom: 15px;">SUMMARY</h2>
            
            ${
              cartDetails?.cart?.discount > 0
                ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span>Subtotal:</span>
                <span>Rs ${totalAmountWithoutDiscount}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px; color: #10b981;">
                <span>Discount (${cartDetails.cart.discount}%):</span>
                <span>-Rs ${Math.round(
                  (totalAmountWithoutDiscount * cartDetails.cart.discount) /
                    100,
                )}</span>
              </div>
            `
                : ""
            }
            
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
              <span>Total Taxable Amount:</span>
              <span>Rs ${totalAmountWithoutTax}</span>
            </div>
            
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
              <span>Total Tax:</span>
              <span>Rs ${totalTax}</span>
            </div>
            
            <div style="border-top: 2px solid #333; padding-top: 15px; display: flex; justify-content: space-between; font-size: 20px; font-weight: bold;">
              <span>TOTAL AMOUNT:</span>
              <span>Rs ${totalAmountWithTax}</span>
            </div>
          `;

      tempContainer.appendChild(summaryDiv);
      await addElementToPdf(summaryDiv, false);
      summaryDiv.remove();

      // Render notes section outside summary box
      if (cartDetails?.cart?.notes) {
        const notesDiv = document.createElement("div");
        notesDiv.style.padding = "10px 0";
        notesDiv.style.width = `${pageWidth - 20}mm`;

        const notesHtmlParts = [];

        if (cartDetails?.cart?.notes) {
          notesHtmlParts.push(`
            <div style="font-size: 16px; margin-top: 6px;">
            <strong>Notes:</strong>
            </div>
            <div style="font-size: 14px; margin-top: 2px;">
            ${cartDetails.cart.notes}
            </div>
            `);
        }

        notesDiv.innerHTML = notesHtmlParts.join("");

        tempContainer.appendChild(notesDiv);
        await addElementToPdf(notesDiv, true);
        notesDiv.remove();
      }

      // Cleanup
      document.body.removeChild(tempContainer);

      // Save PDF
      const cartTitle = cartDetails?.cart?.name || "Cart";
      pdf.save(`${cartTitle.replace(/[^a-z0-9]/gi, "_")}.pdf`);

      toast.success("PDF generated successfully");
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const handleUpdateCartName = async () => {
    try {
      const response = await axios.put(`${API}/Admin/carts/${cartId}`, {
        name: cartName,
      });
      if (response.status === 200) {
        toast.success("Cart name updated successfully");
        setIsEditingName(false);
        getCartDetails();
      } else {
        toast.error("Failed to update cart name");
      }
    } catch (error) {
      console.error("Failed to update cart name:", error);
      toast.error("Failed to update cart name");
    }
  };

  const products = useMemo(() => {
    if (
      !cartDetails ||
      !cartDetails.cart ||
      !cartDetails.cart.products ||
      loading
    )
      return [];
    return cartDetails.cart.products?.map((product) => {
      const productPrice = product.cost || 0;
      return {
        id: product._id,
        name: product.name || "-",
        description: product.description || "-",
        brand: product.brandName || "-",
        itemDetails: product.item_Details || [],
        image: product.defaultImage || product.image?.[0] || "",
        price: Math.round(Number(productPrice) || 0),
        margin: Number(productPrice.margin) || 0,
        discount: cartDetails.cart?.discount || 0,
        quantity: product.quantity || 0,
        gst: product.gst || 0,
        customisation: product.customization || "",
        remarks: product.remarks || "",
      };
    });
  }, [cartDetails, loading]);

  const {
    totalAmountWithTax,
    totalAmountWithoutTax,
    totalTax,
    totalAmountWithoutDiscount,
  } = useMemo(() => {
    if (
      !cartDetails ||
      !cartDetails.cart ||
      !cartDetails.cart.products ||
      loading
    )
      return {
        totalAmountWithTax: 0,
        discount: 0,
        totalTaxableAmount: 0,
        totalTax: 0,
        totalAmountWithoutDiscount: 0,
      };
    const totalAmountWithTax = cartDetails?.cart?.products?.reduce(
      (acc, product) => {
        const discount = cartDetails?.cart?.discount || 0;
        const productPrice = product.cost || 0;
        const cost = Number(productPrice) || 0;
        const discountedCost = cost * (1 - discount / 100);
        const gst = Number(product.gst) || 0;
        const quantity = Number(product.quantity) || 0;
        const amount = discountedCost * (1 + gst / 100);
        return acc + amount * quantity;
      },
      0,
    );
    const totalAmountWithoutDiscount = cartDetails?.cart?.products?.reduce(
      (acc, product) => {
        const productPrice = product.cost || 0;
        const cost = Number(productPrice) || 0;
        const quantity = Number(product.quantity) || 0;
        return acc + cost * quantity;
      },
      0,
    );
    const totalAmountWithoutTax = cartDetails?.cart?.products?.reduce(
      (acc, product) => {
        const discount = cartDetails?.cart?.discount || 0;
        const productPrice = product.cost || 0;
        const cost = Number(productPrice) || 0;
        const discountedCost = cost * (1 - discount / 100);
        const quantity = Number(product.quantity) || 0;
        return acc + discountedCost * quantity;
      },
      0,
    );
    const totalTax = totalAmountWithTax - totalAmountWithoutTax;
    return {
      totalAmountWithTax: Math.round(totalAmountWithTax),
      totalAmountWithoutTax: Math.round(totalAmountWithoutTax),
      totalTax: Math.round(totalTax),
      totalAmountWithoutDiscount: Math.round(totalAmountWithoutDiscount),
    };
  }, [cartDetails, loading]);

  const handleMarkOrder = async ({ orderImages = [], cartId, status }) => {
    try {
      const response = await axios.post(`${API}/Admin/cartOrders/${cartId}`, {
        orderImages,
        status,
      });
      toast.success("Order marked successfully");
      getCartDetails();
    } catch (error) {
      console.log(error);
      toast.error("Failed to mark order");
    }
  };

  const handleOrdersClick = async (type = "confirm") => {
    if (type === "confirm") {
      setShowMarkOrderConfirmation(true);
    } else {
      setShowCancelOrderConfirmation(true);
    }
  };

  const handleCancelOrderCancel = () => {
    setShowCancelOrderConfirmation(false);
  };


  const handleCancelOrderConfirm = async () => {
    setShowCancelOrderConfirmation(false);
    await handleMarkOrder({
      orderImages: [],
      cartId,
      status: "cancelled",
    });
    navigate("/orders");
  };

  const handleConfirmOrder = async () => {
    await handleMarkOrder({
      orderImages: [
        ...(cartDetails?.cart?.orderImages || []),
      ],
      cartId,
      status: "processing",
    });
  };

  const handleMarkOrderCancel = () => {
    setShowMarkOrderConfirmation(false);
  };

  const handleMarkOrderConfirm = async () => {
    setShowMarkOrderConfirmation(false);
    await handleConfirmOrder();
    navigate("/orders");
  };

  return {
    cartId,
    loading,
    cartDetails,
    products,
    totalAmountWithTax,
    totalAmountWithoutTax,
    totalTax,
    totalAmountWithoutDiscount,
    cartNotes,
    setCartNotes,
    cartName,
    setCartName,
    isEditingName,
    setIsEditingName,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    showCancelOrderConfirmation,
    showMarkOrderConfirmation,
    showProductImages,
    setShowProductImages,
    productImages,
    mockupImages,
    defaultImage,
    getCartDetails,
    handleChangeImages,
    updateCartProduct,
    deleteProductFromCart,
    handleCancelDeleteConfirmation,
    handleConfirmDeleteConfirmation,
    handleCancelOrderCancel,
    handleCancelOrderConfirm,
    handleMarkOrderCancel,
    handleMarkOrderConfirm,
    handleAddDiscount,
    handleDownloadPdf,
    handleUpdateCartName,
    handleOrdersClick,
  };
};
